import React, { useRef, useMemo, memo } from "react"
import { motion } from "framer-motion"
import { useLiteModeContext } from "../../hooks/useLiteMode"

interface AnimatedBackgroundProps {
  className?: string
  particleCount?: number
  color?: string
}

/**
 * Премиальный 3D-подобный анимированный фон для Headman
 * Создаёт эффект глубины с плавающими частицами и градиентами
 * Оптимизирован для мобильных устройств
 * 
 * PERFORMANCE: В lite mode полностью отключается или сильно упрощается
 */
const AnimatedBackgroundComponent: React.FC<AnimatedBackgroundProps> = ({
  className = "",
  particleCount = 30,
  color = "#d4af37", // Headman gold
}) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const { isLiteMode, isMobile, prefersReducedMotion } = useLiteModeContext()

  // В lite mode не рендерим ничего
  if (isLiteMode || prefersReducedMotion) {
    return (
      <div
        className={`absolute inset-0 pointer-events-none ${className}`}
        style={{
          background: `radial-gradient(ellipse 80% 50% at 50% 0%, ${color}08 0%, transparent 50%)`,
        }}
      />
    )
  }

  // Уменьшаем количество частиц на мобильных для производительности
  const actualParticleCount = isMobile ? Math.min(particleCount, 8) : particleCount

  // Генерируем частицы один раз
  const particles = useMemo(() => {
    return Array.from({ length: actualParticleCount }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: 2 + Math.random() * 3, // Уменьшили максимальный размер
      duration: 20 + Math.random() * 15, // Замедлили анимацию
      delay: Math.random() * 10,
      opacity: 0.08 + Math.random() * 0.2, // Уменьшили яркость
    }))
  }, [actualParticleCount])

  return (
    <div
      ref={containerRef}
      className={`absolute inset-0 overflow-hidden pointer-events-none ${className}`}
    >
      {/* Основной градиент */}
      <div
        className="absolute inset-0"
        style={{
          background: `
            radial-gradient(ellipse 80% 50% at 50% 0%, ${color}08 0%, transparent 50%),
            radial-gradient(ellipse 60% 40% at 100% 100%, ${color}05 0%, transparent 40%),
            radial-gradient(ellipse 50% 30% at 0% 50%, ${color}03 0%, transparent 30%)
          `,
        }}
      />

      {/* Анимированные волны */}
      <motion.div
        className="absolute inset-0"
        animate={{
          background: [
            `radial-gradient(ellipse 100% 100% at 0% 0%, ${color}06 0%, transparent 50%)`,
            `radial-gradient(ellipse 100% 100% at 100% 100%, ${color}06 0%, transparent 50%)`,
            `radial-gradient(ellipse 100% 100% at 0% 100%, ${color}06 0%, transparent 50%)`,
            `radial-gradient(ellipse 100% 100% at 100% 0%, ${color}06 0%, transparent 50%)`,
            `radial-gradient(ellipse 100% 100% at 0% 0%, ${color}06 0%, transparent 50%)`,
          ],
        }}
        transition={{
          duration: 20,
          repeat: Infinity,
          ease: "linear",
        }}
      />

      {/* Плавающие частицы */}
      {particles.map((particle) => (
        <motion.div
          key={particle.id}
          className="absolute rounded-full"
          style={{
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            width: particle.size,
            height: particle.size,
            background: color,
            opacity: particle.opacity,
            boxShadow: `0 0 ${particle.size * 2}px ${color}`,
          }}
          animate={{
            y: [0, -30, 0],
            x: [0, Math.random() > 0.5 ? 15 : -15, 0],
            opacity: [particle.opacity, particle.opacity * 1.5, particle.opacity],
            scale: [1, 1.2, 1],
          }}
          transition={{
            duration: particle.duration,
            delay: particle.delay,
            repeat: Infinity,
            ease: "easeInOut",
          }}
        />
      ))}

      {/* Горизонтальные линии (барбершоп стиль) */}
      <div className="absolute inset-0 opacity-[0.02]">
        {[...Array(8)].map((_, i) => (
          <motion.div
            key={i}
            className="absolute left-0 right-0 h-px"
            style={{
              top: `${12.5 * (i + 1)}%`,
              background: `linear-gradient(90deg, transparent, ${color}, transparent)`,
            }}
            animate={{
              opacity: [0.3, 0.6, 0.3],
              scaleX: [0.8, 1, 0.8],
            }}
            transition={{
              duration: 4 + i,
              delay: i * 0.5,
              repeat: Infinity,
              ease: "easeInOut",
            }}
          />
        ))}
      </div>

      {/* Центральное свечение - без blur на мобильных для производительности */}
      <motion.div
        className="absolute left-1/2 top-1/3 -translate-x-1/2 -translate-y-1/2 w-96 h-96 rounded-full"
        style={{
          background: `radial-gradient(circle, ${color}06 0%, transparent 70%)`,
          filter: isMobile ? "none" : "blur(30px)", // Убираем blur на мобильных
        }}
        animate={{
          scale: [1, 1.1, 1], // Уменьшили амплитуду
          opacity: [0.4, 0.6, 0.4],
        }}
        transition={{
          duration: 10, // Замедлили
          repeat: Infinity,
          ease: "easeInOut",
        }}
      />

      {/* Noise overlay для текстуры */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.7' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        }}
      />
    </div>
  )
}

// Мемоизируем компонент для предотвращения лишних ре-рендеров
export const AnimatedBackground = memo(AnimatedBackgroundComponent)

